/**
 * The ONE product screen — `/countries/:cc/admin/products` (roles/products design §2,
 * owner ruling 2026-08-11) — REBUILT AS A TABLE + DRAWER under owner ruling N-54
 * (2026-09-17): "No schema or API change; presentation only."
 *
 * ===========================================================================
 * N-54 — WHAT THE REBUILD CHANGED, ITEM BY ITEM
 * ===========================================================================
 *  1. DEFAULT COUNTRY = THE HEADER'S CURRENT COUNTRY, not "All". The group admin's
 *     pill still offers "All countries" (the interleaved view with a country badge per
 *     row, OD-G) but the page opens on the URL's country — the one the header shows.
 *  2. A REGISTER, not a card grid: code · name (+ readiness badge) · class · shelf ·
 *     price / from · term · status, on register rows (the owner's OQ-1 density
 *     ruling — the same utility every register wears). RETIRED ROWS ARE HIDDEN BY
 *     DEFAULT behind "Show retired products". Search + class + shelf filters, all
 *     CLIENT-SIDE over the page the list route already returns, so the wire and the
 *     query keys are exactly the v22-C ones.
 *  3. THE ROW OPENS A RIGHT-SIDE DRAWER (`ProductDrawer`) holding every edit the card
 *     and its two modals carried; Change price is a view INSIDE that drawer. The 40-word
 *     classification explanation lives once, beside the select, as help text.
 *  4. "Add product" is the toolbar's primary button and opens the same drawer in its
 *     create view; "Save as PDF" is the labelled toolbar button (the browser's print,
 *     named for what it does); Shelves is the secondary
 *     tab beside Products.
 *  5. EVERY BEHAVIOUR BELOW SURVIVES VERBATIM and stays pinned by
 *     `products-page.test.tsx` (selectors moved with the furniture, never the claims):
 *     · the `config.manage`-in-active-country gate, grp admitted (R-A contrast pair);
 *     · the per-country model: every row belongs to one country, a create lands in
 *       `workCc`, a price PUT targets the ROW's country (`COUNTRY_CODE_BY_ID`);
 *     · the THREE PRICE STATES that must never look alike (invitation / gap sentence /
 *       real amount, 0.00 included), built on `/history`, `written` never `data`;
 *     · masking: the server serves `•••` to a seat without `amounts.view`; the cell shows
 *       the marker in the money's own slot (hidden, not blanked — no column disappears)
 *       and the form never prefills a masked value back into a PUT;
 *     · the permanent code (no PATCH carries `code` or `country`; retire + re-create);
 *     · the unconfirmed name stays off quotes until confirmed, after classification;
 *     · dated price versions — a change opens a new version, never overwrites;
 *     · the rename confirm with its three honest edges; classification for `grp:true`
 *       only; confirm-the-name disabled with the reason; create's safety net (`kind`
 *       and `verify_before_prod` never POSTed, the 409 verbatim); shelves inline.
 *
 * ⚠ THE DRAWER IS THE FIRST SIDE PANEL SINCE WT5 KILLED THE INQUIRY SLIDE-OVER
 * (`93377af1`, owner ruling pair "forms as full pages + the slide-over killed"). N-54 is
 * a page-scoped later ruling and the drawer cites it in its own header; nothing here
 * reopens WT5 for the inquiry forms.
 *
 * ⚠ "SAVE AS PDF" IS THE BROWSER'S PRINT, and the label says so. There is no
 * catalogue PDF route on the server (only `/quotes/:id/pdf`) and N-54 forbids adding one;
 * `buildPrintDocument` opens the visible rows in a print window where "Save as PDF" is
 * the operator's choice. A server-rendered price list is an owner decision, filed.
 *
 * ===========================================================================
 * ⚠ THE THREE PRICE STATES THAT MUST NEVER LOOK ALIKE
 * ===========================================================================
 *   NOT PRICED HERE   no versions at all → "No price row — this country cannot
 *                     quote it." — an invitation, with the Add control in the drawer.
 *   NO PRICE TODAY    versions exist, none current → the gap sentence, in the warning
 *                     tint (never a bare foreground: a status colour is legal only inside
 *                     its own tint — and the class the old card used, `text-danger`, was a
 *                     phantom `app.css` never declared).
 *   PRICED AT 0.00    a real resolved price → rendered as an AMOUNT, never a dash.
 *
 * ===========================================================================
 * ⚠ SINCE MIGRATION 46, CONFIRMATION IS NOT THE FIRST ACT — CLASSIFICATION IS
 * ===========================================================================
 * The server refuses to clear `verify_before_prod` on an `unclassified` row
 * (`assertClassifiedBeforeProd`, ruling Q4 iii); "Confirm the name" is DISABLED with
 * the reason on it for such a row. A Country Admin creates, names and prices; a
 * GROUP ADMIN classifies before the name can be confirmed; until then the row stays
 * off every quote, PDF and sales order.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, Search, X } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Chip,
  CountryBadge,
  EmptyState,
  SkeletonRows,
  Tooltip,
} from '../../components/ui';
import { useSession } from '../../auth/session';
import { useCountryCode } from '../../auth/guards';
import { COUNTRY_ORDER } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';
import { docToneClasses } from '../../domain/quotes';
import { cn } from '../../lib/cn';
import { isMaskedAmount } from '../inquiries/format';
import {
  COUNTRY_CODE_BY_ID,
  PRODUCT_KINDS,
  PRODUCT_KIND_LABEL,
  adminCatalogueKeys,
  createProduct,
  createProductGroup,
  deleteProductGroup,
  fetchAllProducts,
  fetchCountryCurrencies,
  fetchPriceHistory,
  fetchProductGroups,
  fetchProducts,
  putPrices,
  updateProduct,
  updateProductGroup,
} from './catalogue-api';
import type { CountryProductDto, ProductDto, ProductKind, UpdateProductBody } from './catalogue-api';
import { ConfirmDialog } from './ConfirmDialog';
import { ProductDrawer } from './ProductDrawer';
import type { CreateDraft, DraftPrice, DrawerView } from './ProductDrawer';
import {
  EMPTY_FILTERS,
  buildPrintDocument,
  filterProducts,
  groupHistory,
  hasActiveFilter,
  listsByProduct,
  previewPriceChange,
  priceText,
} from './products-model';
import type { PrintRow, ProductFilters, ProductHistory } from './products-model';
import { ShelvesPanel } from './ShelvesPanel';
import type { ShelfAction } from './ShelvesPanel';
import { NoAdminAccess, errorSentence } from './states';

/** A rename waiting on the operator to read the three edges and confirm. */
interface PendingRename {
  id: string;
  code: string;
  from: string;
  to: string;
}

type Tab = 'products' | 'shelves';

/**
 * What the price cell knows about a row: its lists, or that the history is not in yet.
 * `lists` is empty for a product with no price row in its country; one entry per currency
 * list otherwise (migration 66 — Congo can hold a CDF list and a USD list).
 */
type PriceState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | { kind: 'ready'; lists: readonly ProductHistory[] };

export function ProductsPage(): React.JSX.Element {
  const { can, isGroupAdmin } = useSession();
  const countryCode = useCountryCode();
  /*
   * The routes' own gate, kept verbatim from both predecessor screens: `config.manage`
   * in the ACTIVE country, grp admitted. The R-A contrast pair (a Country Admin reaches
   * their own catalogue; the same seat is refused another country's) holds unchanged.
   */
  const permitted = isGroupAdmin || can(countryCode, 'config.manage');
  const queryClient = useQueryClient();

  const [actionError, setActionError] = useState<string | null>(null);
  const [savedPrices, setSavedPrices] = useState<CountryProductDto[] | null>(null);
  /**
   * WHICH PRODUCT'S DRAWER IS OPEN, BY ID AND NEVER BY SNAPSHOT — the drawer derives its
   * product from the live query rows, so a PATCH landing (classify, confirm, retire)
   * redraws it from what the server now holds instead of a stale copy taken at open time.
   */
  const [manageId, setManageId] = useState<string | null>(null);
  /** The create view of the same drawer, open only at decision time. */
  const [createOpen, setCreateOpen] = useState(false);
  /** Inline product-name edit, inside the drawer, one product at a time. */
  const [nameEdit, setNameEdit] = useState<{ id: string; value: string } | null>(null);
  const [pendingRename, setPendingRename] = useState<PendingRename | null>(null);
  const [priceDraft, setPriceDraft] = useState<DraftPrice | null>(null);
  const [createDraft, setCreateDraft] = useState<CreateDraft>({ name: '', code: '', shelf: '', term: false });
  const [tab, setTab] = useState<Tab>('products');

  /**
   * N-54 item 1 — THE PILL DEFAULTS TO THE HEADER'S COUNTRY, AND FOLLOWS IT. `useCountryCode()`
   * is the URL's `:cc`, the country the top bar shows; "All countries" is the interleaved
   * view a group admin can still choose. A non-grp seat never sees the pill: their view IS
   * the active country.
   *
   * ⚠ THE CHOICE IS KEYED TO THE COUNTRY IT WAS MADE IN. The route element stays mounted when
   * the top bar switches country (`CountrySwitcher` navigates to the same path under a new
   * `:cc`), so a value captured once at mount would leave the page on the OLD country while
   * the header shows the new one — the review reproduced exactly that. Deriving the pill from
   * `{for, value}` during render (React's "adjust state when a prop changes" shape, no effect,
   * no extra render) makes a header switch land on the header's country every time.
   */
  const [pillChoice, setPillChoice] = useState<{ for: CountryCode; value: CountryCode | 'ALL' }>({
    for: countryCode,
    value: countryCode,
  });
  const pill: CountryCode | 'ALL' = pillChoice.for === countryCode ? pillChoice.value : countryCode;
  const viewCc: CountryCode | null = isGroupAdmin ? (pill === 'ALL' ? null : pill) : countryCode;
  /** The country a create or a shelf write lands in — never `'ALL'`. */
  const workCc: CountryCode = viewCc ?? countryCode;

  /**
   * The search, class and retired filters are country-agnostic and survive a country
   * change; the SHELF filter is not — a group id belongs to one country's catalogue — so it
   * is keyed to the country it was chosen in and reads as "any shelf" anywhere else. Without
   * this, a shelf chosen in CD kept filtering AO's rows (and the ALL view) with the control
   * that explained the absence no longer on screen — the review reproduced that too.
   */
  const [filters, setFilters] = useState<ProductFilters>(EMPTY_FILTERS);
  const [shelfChoice, setShelfChoice] = useState<{ for: CountryCode | null; id: string }>({ for: viewCc, id: '' });
  const shelfFilter = shelfChoice.for === viewCc ? shelfChoice.id : '';
  const effectiveFilters: ProductFilters = useMemo(() => ({ ...filters, shelf: shelfFilter }), [filters, shelfFilter]);

  const products = useQuery({
    queryKey:
      viewCc === null ? adminCatalogueKeys.allProducts() : adminCatalogueKeys.products(viewCc),
    queryFn: ({ signal }) =>
      viewCc === null ? fetchAllProducts(signal) : fetchProducts(viewCc, signal),
    enabled: permitted,
  });

  /*
   * Shelves are fetched for a SINGLE country in view. The interleaved view renders shelf
   * NAMES from the wire (`product_group_name`) but offers no re-filing control: a shelf
   * has no life apart from the one catalogue it files, and a six-country select would
   * need six group lists to say anything true.
   */
  const groups = useQuery({
    queryKey: adminCatalogueKeys.productGroups(workCc),
    queryFn: ({ signal }) => fetchProductGroups(workCc, signal),
    enabled: permitted && viewCc !== null,
  });

  /** Price history per country in view — one query in the single-country view, six in ALL. */
  const historyCountries: readonly CountryCode[] = useMemo(
    () => (viewCc === null ? COUNTRY_ORDER : [viewCc]),
    [viewCc],
  );
  const histories = useQueries({
    queries: historyCountries.map((cc) => ({
      queryKey: adminCatalogueKeys.priceHistory(cc),
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchPriceHistory(cc, signal),
      enabled: permitted,
    })),
  });
  const priceByCountry = useMemo(() => {
    const map = new Map<CountryCode, Map<string, ProductHistory[]>>();
    historyCountries.forEach((cc, index) => {
      const rows = histories[index]?.data;
      if (rows === undefined) return;
      map.set(cc, listsByProduct(groupHistory(rows)));
    });
    return map;
  }, [histories, historyCountries]);

  /**
   * The currencies each country in view quotes in (migration 66) — the lists the drawer offers.
   * Until it answers (or if it cannot), the drawer offers the lists the product already has,
   * and the server still refuses any currency outside the country's set.
   */
  const currencyQueries = useQueries({
    queries: historyCountries.map((cc) => ({
      queryKey: adminCatalogueKeys.currencies(cc),
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchCountryCurrencies(cc, signal),
      enabled: permitted,
      staleTime: 5 * 60_000,
    })),
  });
  const currenciesOf = (cc: CountryCode, lists: readonly ProductHistory[]): string[] => {
    const known = currencyQueries[historyCountries.indexOf(cc)]?.data?.currencies;
    if (known !== undefined && known.length > 0) return known;
    return [...new Set(lists.map((list) => list.currency))];
  };

  const invalidateCatalogue = (): void => {
    // Prefix keys, so the ALL view, every per-country list and every price history
    // re-read together — a rename or a re-price is visible wherever the row appears.
    void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'product-groups'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'country-products'] });
  };

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateProductBody }) => updateProduct(id, body),
    onSuccess: () => {
      setActionError(null);
    },
    onError: (error: unknown) => {
      setActionError(errorSentence(error, 'That change could not be saved.'));
    },
    // ON SETTLED, NOT ON SUCCESS — the checkbox-snapback defect the predecessor screen
    // documented: a rejected save must re-read the list so controls snap back to what the
    // server actually holds.
    onSettled: invalidateCatalogue,
  });

  const create = useMutation({
    mutationFn: () =>
      createProduct({
        country: workCc,
        code: createDraft.code.trim(),
        name: createDraft.name.trim(),
        // `undefined` when unshelved — an absent field, not a null request, at create.
        ...(createDraft.shelf === '' ? {} : { product_group_id: createDraft.shelf }),
        ...(createDraft.term ? { term_bearing: true } : {}),
      }),
    onSuccess: () => {
      setActionError(null);
      setCreateDraft({ name: '', code: '', shelf: '', term: false });
      // A successful create closes the drawer; a refusal keeps it open with the server's
      // sentence rendered inside it, so the collision is read where the retype happens.
      setCreateOpen(false);
    },
    onError: (error: unknown) => {
      // The per-country code collision (409) arrives as the server's own sentence, which
      // names the country and the row in the way — rendered verbatim, never rewritten.
      setActionError(errorSentence(error, 'The product could not be created.'));
    },
    onSettled: invalidateCatalogue,
  });

  const shelves = useMutation({
    mutationFn: async (action: ShelfAction) => {
      if (action.kind === 'create') return createProductGroup({ country: workCc, name: action.name });
      if (action.kind === 'rename') return updateProductGroup(action.id, { name: action.name });
      return deleteProductGroup(action.id);
    },
    onSuccess: () => {
      setActionError(null);
    },
    onError: (error: unknown) => {
      setActionError(errorSentence(error, 'That shelf could not be saved.'));
    },
    onSettled: invalidateCatalogue,
  });

  const savePrice = useMutation({
    mutationFn: (entry: DraftPrice) =>
      putPrices(entry.cc, [
        {
          product_id: entry.productId,
          // `null` is the explicit BLANK — priced per customer (R3). Never '' and never 0.
          price: entry.perCustomer ? null : entry.price.trim(),
          currency: entry.currency.trim().toUpperCase(),
          ...(entry.effectiveFrom.trim() === '' ? {} : { effective_from: entry.effectiveFrom.trim() }),
          // Only ever CLEARS — a screen that could set the flag back on would re-open a
          // review item nobody asked to re-open.
          ...(entry.clearFlag ? { verify_before_prod: false } : {}),
        },
      ]),
    onSuccess: (result) => {
      setActionError(null);
      setPriceDraft(null);
      // The drawer closes with the save, as the price modal did: the confirmation is the
      // page's "Saved" alert, and it would be hidden behind an open panel. EVERY draft the
      // drawer held goes with it — a name half-typed before "Change price" must not resurface
      // the next time the row is opened (the review reproduced that resurfacing).
      setManageId(null);
      setNameEdit(null);
      // `written`, never `data` — a future-dated price is absent from today's list, and
      // rendering `data` would show the operator nothing after a successful save.
      setSavedPrices(result.written);
      invalidateCatalogue();
    },
    onError: (error: unknown) => {
      setActionError(errorSentence(error, 'The price could not be saved.'));
    },
  });

  const allRows = useMemo(() => products.data?.data ?? [], [products.data]);
  const shelfRows = useMemo(() => (viewCc === null ? [] : (groups.data ?? [])), [viewCc, groups.data]);
  const filtered = useMemo(
    () => filterProducts(allRows, shelfRows, effectiveFilters),
    [allRows, shelfRows, effectiveFilters],
  );
  /**
   * The price histories are separate queries from the list, one per country in view. Until
   * every one has answered, a row's price is UNKNOWN — not "not priced here" — and nothing
   * that prints prices may run: a catalogue exported while a history was still in flight
   * would go to paper reading "Not priced here" on every row (the review's finding).
   */
  const historiesReady = histories.every((query) => query.data !== undefined);
  const historyState = (product: ProductDto): PriceState => {
    const cc = COUNTRY_CODE_BY_ID[product.country_id] ?? workCc;
    const query = histories[historyCountries.indexOf(cc)];
    if (query === undefined || query.isPending) return { kind: 'pending' };
    if (query.isError) return { kind: 'error' };
    return { kind: 'ready', lists: priceByCountry.get(cc)?.get(product.id) ?? [] };
  };
  /** `''` is Ungrouped — a `<select>` option value cannot be `null`. */
  const shelfOptions = useMemo(
    () => [
      { value: '', label: 'Ungrouped' },
      ...shelfRows.filter((group) => group.active).map((group) => ({ value: group.id, label: group.name })),
    ],
    [shelfRows],
  );

  /** The drawer's product, derived LIVE from the rows by id. */
  const managed = useMemo(
    () => (manageId === null ? null : (allRows.find((row) => row.id === manageId) ?? null)),
    [manageId, allRows],
  );

  /*
   * The preview (P2, T-P2): a read-only mirror of the server's close-incumbent /
   * open-successor procedure, in sentences. The client's day words the tenses only —
   * a blank date's real "today" is the server's, in the country's timezone, and the
   * first sentence says exactly that.
   */
  const previewSentences =
    priceDraft === null
      ? []
      : previewPriceChange(
          // The draft's OWN list only (migration 66): a USD write is never described as
          // closing the CDF price, because it does not.
          priceByCountry
            .get(priceDraft.cc)
            ?.get(priceDraft.productId)
            ?.find((list) => list.currency === priceDraft.currency) ?? null,
          priceDraft,
          new Date().toISOString().slice(0, 10),
        );

  /**
   * "Hidden, not blanked": the server already serves `•••` in the money slot to a seat
   * without `amounts.view`; this line names the reason once, under the table, keyed on
   * the PERMISSION (the house rule: never a string compare against the glyph).
   */
  const amountsHidden = !isGroupAdmin && historyCountries.some((cc) => !can(cc, 'amounts.view'));

  if (!permitted) return <NoAdminAccess />;

  function listsFor(product: ProductDto): readonly ProductHistory[] {
    const state = historyState(product);
    return state.kind === 'ready' ? state.lists : [];
  }

  function clearFilters(): void {
    setFilters({ ...EMPTY_FILTERS, showRetired: filters.showRetired });
    setShelfChoice({ for: viewCc, id: '' });
  }

  function openRow(product: ProductDto): void {
    setActionError(null);
    setPriceDraft(null);
    setCreateOpen(false);
    setManageId(product.id);
  }

  function closeDrawer(): void {
    setManageId(null);
    setNameEdit(null);
    setPriceDraft(null);
    setCreateOpen(false);
  }

  function beginPrice(product: ProductDto, from: CountryProductDto | null, currency: string): void {
    setSavedPrices(null);
    setActionError(null);
    setPriceDraft({
      productId: product.id,
      productCode: product.code,
      cc: COUNTRY_CODE_BY_ID[product.country_id] ?? workCc,
      /*
       * A MASKED PRICE IS NEVER PREFILLED BACK INTO THE FORM. `•••` is not a number, and
       * round-tripping it into a PUT would either 422 or — far worse on a money field —
       * be silently coerced somewhere. A seat that cannot see the price starts from
       * blank and must type one deliberately. A blank (per-customer) price has nothing to
       * prefill either.
       */
      price:
        from === null || from.price === null || isMaskedAmount(from.price) ? '' : from.price,
      // The LIST the opening button named (migration 66) — never typed.
      currency: from?.currency ?? currency,
      effectiveFrom: '',
      clearFlag: false,
      perCustomer: from?.priced_per_customer === true,
    });
  }

  function commitRename(): void {
    if (pendingRename === null) return;
    patch.mutate({ id: pendingRename.id, body: { name: pendingRename.to } });
    setPendingRename(null);
    setNameEdit(null);
  }

  function exportPdf(): void {
    const printRows: PrintRow[] = filtered.rows.map((product) => {
      const lists = listsFor(product);
      return {
        country: COUNTRY_CODE_BY_ID[product.country_id] ?? '',
        code: product.code,
        name: product.name,
        classification: PRODUCT_KIND_LABEL[product.kind as ProductKind] ?? product.kind,
        shelf: product.product_group_name ?? '',
        // One entry per list (migration 66) — "CDF …; USD …" for a two-list product.
        price:
          lists.length === 0
            ? 'Not priced here'
            : lists
                .map((list) =>
                  list.current === null
                    ? `${lists.length > 1 ? `${list.currency}: ` : ''}No price applies today`
                    : `${priceText(list.current)} from ${list.current.effective_from}`,
                )
                .join('; '),
        term: product.term_bearing ? 'Term' : '',
        status: product.active ? 'Active' : 'Withdrawn',
      };
    });
    const html = buildPrintDocument({
      title: viewCc === null ? 'Products — every country' : `Products — ${viewCc}`,
      subtitle: `${String(printRows.length)} product${printRows.length === 1 ? '' : 's'}`,
      printedOn: new Date().toISOString().slice(0, 10),
      showCountry: viewCc === null,
      rows: printRows,
    });
    const popup = window.open('', '_blank');
    if (popup === null) {
      setActionError('The print window was blocked — allow pop-ups for this site and try again.');
      return;
    }
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    popup.print();
  }

  const drawerView: DrawerView | null = createOpen
    ? { kind: 'create' }
    : managed === null
      ? null
      : {
          kind: 'edit',
          product: managed,
          lists: listsFor(managed),
          currencies: currenciesOf(
            COUNTRY_CODE_BY_ID[managed.country_id] ?? workCc,
            listsFor(managed),
          ),
          cc: COUNTRY_CODE_BY_ID[managed.country_id] ?? workCc,
        };
  const drawerOpen = drawerView !== null;

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Products"
        subtitle={
          viewCc === null
            ? 'Every country’s own catalogue, interleaved — names, shelves and prices in one place. Pick a country to add products or file shelves.'
            : `Everything ${viewCc} sells, and for how much — one screen. Another country may sell a product with the same code, and it is a different product. Prices are dated: changing one opens a new version rather than overwriting the old.`
        }
        actions={
          <div className="flex items-center gap-2">
            {/* Secondary, and LABELLED WITH WHAT IT DOES: "Save as PDF" is the browser's own
              * term for the print destination this opens, so the label carries the fact and
              * the tooltip only rephrases it (Tooltip.tsx: never the only carrier). Disabled
              * until every price history has answered — a print taken mid-load would read
              * "Not priced here" on every row. */}
            <Tooltip content="Opens your browser’s print view of the rows shown — choose “Save as PDF” there.">
              <Button
                variant="ghost"
                onClick={exportPdf}
                disabled={products.isPending || !historiesReady || filtered.rows.length === 0}
              >
                <Printer className="size-4" aria-hidden="true" />
                Save as PDF
              </Button>
            </Tooltip>
            <Button
              onClick={() => {
                setActionError(null);
                setManageId(null);
                setPriceDraft(null);
                setCreateOpen(true);
              }}
            >
              Add product
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-4 px-6">
        {/* ── the toolbar: one band — tabs left, the utilities right ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div role="tablist" aria-label="Catalogue sections" className="flex w-fit items-center gap-1 rounded-sm border border-line bg-subtle p-1">
              {(
                [
                  ['products', 'Products'],
                  ['shelves', 'Shelves'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  onClick={() => {
                    setTab(id);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-3 py-1.5 text-body',
                    tab === id ? 'bg-surface font-medium text-fg shadow-tab-active' : 'text-fg-secondary hover:text-fg',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {isGroupAdmin ? (
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Country filter">
                {(['ALL', ...COUNTRY_ORDER] as const).map((option) => (
                  <Button
                    key={option}
                    variant={pill === option ? undefined : 'quiet'}
                    onClick={() => {
                      setPillChoice({ for: countryCode, value: option });
                      closeDrawer();
                    }}
                  >
                    {option === 'ALL' ? 'All countries' : option}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>

          {tab === 'products' ? (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5">
                <Search className="size-3.5 shrink-0 text-fg-secondary" aria-hidden="true" />
                <input
                  type="search"
                  value={filters.search}
                  aria-label="Search products"
                  placeholder="Search code or name"
                  onChange={(event) => {
                    setFilters({ ...filters, search: event.target.value });
                  }}
                  className="w-[200px] min-w-0 bg-transparent text-body13 text-fg placeholder:text-fg-secondary"
                />
                {filters.search === '' ? null : (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className="shrink-0 text-fg-secondary hover:text-fg"
                    onClick={() => {
                      setFilters({ ...filters, search: '' });
                    }}
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>
              <select
                aria-label="Filter by class"
                value={filters.kind}
                onChange={(event) => {
                  setFilters({ ...filters, kind: event.target.value });
                }}
                className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
              >
                <option value="">Any class</option>
                {PRODUCT_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {/* 'Unclassified' here, not the cell's 'Not classified': the class column
                      * already says it, and an option repeating the exact phrase would read
                      * as a second row to anyone scanning the page. */}
                    {kind === 'unclassified' ? 'Unclassified' : PRODUCT_KIND_LABEL[kind]}
                  </option>
                ))}
              </select>
              {shelfRows.length === 0 ? null : (
                <select
                  aria-label="Filter by shelf"
                  value={shelfFilter}
                  onChange={(event) => {
                    setShelfChoice({ for: viewCc, id: event.target.value });
                  }}
                  className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
                >
                  <option value="">Any shelf</option>
                  {shelfRows.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                  <option value="unfiled">Unfiled</option>
                </select>
              )}
              <Checkbox
                label="Show retired products"
                checked={filters.showRetired}
                onChange={(event) => {
                  setFilters({ ...filters, showRetired: event.target.checked });
                }}
              />
            </div>
          ) : null}
        </div>

        {/* The page-level refusal renders only while the drawer does not own it — the
          * drawer paints `actionError` beside the control being retyped, and the same
          * sentence twice on one screen is noise. */}
        {actionError === null || drawerOpen ? null : (
          <Alert tone="danger" title="That did not save">
            <p>{actionError}</p>
          </Alert>
        )}

        {savedPrices === null ? null : (
          <Alert tone="info" title="Saved">
            <ul className="mt-1 space-y-1">
              {savedPrices.map((row) => (
                // Keyed by the row's whole identity since migration 66 — one PUT may write a
                // product's CDF and USD prices for the same day.
                <li key={`${row.product_id}-${row.currency}-${row.effective_from}`}>
                  {row.product_code} — {priceText(row)} from {row.effective_from}
                  {row.effective_from > new Date().toISOString().slice(0, 10)
                    ? ' (queued — it does not apply yet)'
                    : ''}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {tab === 'shelves' ? (
          /*
           * SHELVES — absorbed here per the owner's ruling (OD-A), now the secondary tab
           * (N-54 item 4). Single-country only: the ALL view says where to go instead of
           * showing six countries' shelves at once.
           */
          <Card title="Shelves">
            <ShelvesPanel
              viewCc={viewCc}
              groups={groups}
              busy={shelves.isPending}
              onAction={(action, done) => {
                shelves.mutate(action, { onSuccess: done });
              }}
            />
          </Card>
        ) : products.isPending ? (
          <div className="rounded-md border border-line bg-surface px-4">
            <SkeletonRows rows={6} label="Loading products…" />
          </div>
        ) : products.isError ? (
          <Card>
            <EmptyState
              title="Could not load the products"
              description={errorSentence(products.error, 'The catalogue could not be read.')}
            />
          </Card>
        ) : allRows.length === 0 ? (
          <Card>
            <EmptyState
              title="No products yet"
              description="A product arrives with its name unconfirmed and stays off quotes until someone confirms it."
              action={
                <Button
                  onClick={() => {
                    setCreateOpen(true);
                  }}
                >
                  Add product
                </Button>
              }
            />
          </Card>
        ) : filtered.rows.length === 0 ? (
          /* Zero rows BECAUSE of a filter is a different state from an empty catalogue:
           * the filters stay, the sentence says so, and the way out is one click. */
          <Card>
            <EmptyState
              title={
                hasActiveFilter(effectiveFilters)
                  ? 'No products match these filters'
                  : `Every product in ${viewCc ?? 'view'} is retired`
              }
              description={
                hasActiveFilter(effectiveFilters)
                  ? `${String(filtered.filteredOut)} of ${String(allRows.length)} hidden by the search or filters` +
                    (filtered.retiredHidden > 0 ? `, ${String(filtered.retiredHidden)} retired hidden.` : '.')
                  : 'Retired products are hidden by default.'
              }
              action={
                /* Both ways out when both apply — a search that matches only a retired row
                 * is "no match" only until the retired rows are shown. */
                <div className="flex flex-wrap justify-center gap-2">
                  {hasActiveFilter(effectiveFilters) ? (
                    <Button variant="ghost" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  ) : null}
                  {filtered.retiredHidden > 0 ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setFilters({ ...filters, showRetired: true });
                      }}
                    >
                      Show retired products
                    </Button>
                  ) : null}
                </div>
              }
            />
          </Card>
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border border-line bg-surface">
              <table className="w-full min-w-[1040px] border-collapse text-left text-body13">
                <caption className="sr-only">
                  Products in {viewCc ?? 'every country'} — code, name, class, shelf, price, term and status. A row opens its editor.
                </caption>
                <thead>
                  <tr className="h-10 border-b border-line bg-subtle">
                    {viewCc === null ? <Th className="w-[72px]">Country</Th> : null}
                    <Th className="w-[200px]">Code</Th>
                    <Th>Name</Th>
                    <Th className="w-[150px]">Class</Th>
                    <Th className="w-[140px]">Shelf</Th>
                    <Th className="w-[280px] text-right">Price / from</Th>
                    <Th className="w-[72px]">Term</Th>
                    <Th className="w-[110px]">Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.rows.map((product) => {
                    const cc = COUNTRY_CODE_BY_ID[product.country_id];
                    const selected = managed?.id === product.id;
                    return (
                      /*
                       * THE ROW IS THE OPENER (N-54 item 3) and the code cell's button is
                       * its keyboard and screen-reader address — named by the code, the
                       * way a register row is named by its document number. The 3px rail
                       * (light) / indigo fill (dark) marks the row whose drawer is open —
                       * a fill in light would break the masked-money marker's contrast.
                       */
                      <tr
                        key={product.id}
                        aria-label={product.code}
                        aria-selected={selected}
                        onClick={() => {
                          openRow(product);
                        }}
                        className={cn('crf-register-row group/row relative cursor-pointer border-b border-line last:border-b-0', selected ? 'bg-surface dark:bg-indigo/20' : 'hover:bg-subtle')}
                      >
                        {/* The rail sits in the row's FIRST cell — the Country cell in the
                          * interleaved view, the Code cell otherwise — so it marks the row's
                          * left edge and never floats between two columns (A2, §5.1). */}
                        {viewCc === null ? (
                          <td className="relative px-3 align-middle">
                            {selected ? <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-indigo" /> : null}
                            {cc === undefined ? null : <CountryBadge code={cc} />}
                          </td>
                        ) : null}
                        <td className="relative px-3 align-middle">
                          {selected && viewCc !== null ? (
                            <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-indigo" />
                          ) : null}
                          <button
                            type="button"
                            className="font-mono text-body text-fg underline-offset-2 hover:underline"
                            onClick={(event) => {
                              event.stopPropagation();
                              openRow(product);
                            }}
                          >
                            {product.code}
                          </button>
                        </td>
                        <td className="min-w-0 px-3 align-middle">
                          <div className="flex min-w-0 items-center gap-2">
                            {/* The NAME goes on customer documents — the strong cell. */}
                            <span className="truncate font-medium text-fg" title={product.name}>
                              {product.name}
                            </span>
                            {product.verify_before_prod ? (
                              <span
                                className={cn(
                                  'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill px-2 py-0.5 text-label font-medium',
                                  docToneClasses('warning'),
                                )}
                              >
                                <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
                                Name not confirmed
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 align-middle text-fg">
                          {/* Readable by every seat; the select is the drawer's, grp-only. An
                            * unknown value from a newer server falls through to itself. */}
                          {PRODUCT_KIND_LABEL[product.kind as ProductKind] ?? product.kind}
                        </td>
                        <td className="px-3 align-middle text-fg-secondary">{product.product_group_name ?? '—'}</td>
                        <td className="px-3 text-right align-middle">
                          <PriceCell state={historyState(product)} />
                        </td>
                        <td className="px-3 align-middle text-fg-secondary">{product.term_bearing ? 'Term' : ''}</td>
                        <td className="px-3 align-middle">
                          {product.active ? (
                            <span className="text-fg-secondary">Active</span>
                          ) : (
                            <span className={cn('inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-label font-medium', docToneClasses('neutral'))}>
                              <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
                              Withdrawn
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* The footer is a sentence: what is shown, what is not, and the way out. INSIDE
                * the card — `text-fg-secondary` measures 4.10 on the bare canvas, a pairing
                * tokens.css forbids, and 4.64 on `bg-surface`. The link is `text-indigo-ink`,
                * the token the repo moved 12px indigo text to (accent/indigo measures 4.22). */}
              <p className="tnum border-t border-line px-4 py-2 text-label text-fg-secondary">
                {filtered.rows.length} of {allRows.length} {allRows.length === 1 ? 'product' : 'products'}
                {filtered.retiredHidden > 0 ? ` · ${String(filtered.retiredHidden)} retired hidden` : ''}
                {filtered.filteredOut > 0 ? (
                  <>
                    {` · ${String(filtered.filteredOut)} hidden by filters · `}
                    <button type="button" className="text-indigo-ink underline-offset-2 hover:underline" onClick={clearFilters}>
                      Clear filters
                    </button>
                  </>
                ) : null}
                {amountsHidden ? ' · Amounts are hidden for this account.' : ''}
              </p>
            </div>
          </>
        )}
      </div>

      {drawerView === null ? null : (
        <ProductDrawer
          view={drawerView}
          workCc={workCc}
          shelfOptions={viewCc === null ? null : shelfOptions}
          hasShelves={shelfRows.length > 0}
          canClassify={isGroupAdmin}
          busy={patch.isPending || create.isPending || savePrice.isPending}
          actionError={actionError}
          nameEdit={managed !== null && nameEdit?.id === managed.id ? nameEdit.value : null}
          onNameEdit={(value) => {
            if (managed === null) return;
            setNameEdit(value === null ? null : { id: managed.id, value });
          }}
          onNameSave={(next) => {
            if (managed === null) return;
            setPendingRename({ id: managed.id, code: managed.code, from: managed.name, to: next });
            // ONE DIALOG AT A TIME: the rename confirm replaces the drawer rather than
            // stacking on it — two open dialogs is two focus traps.
            setManageId(null);
          }}
          onPatch={(body) => {
            if (managed === null) return;
            patch.mutate({ id: managed.id, body });
          }}
          priceDraft={priceDraft}
          previewSentences={previewSentences}
          onBeginPrice={(from, currency) => {
            if (managed === null) return;
            beginPrice(managed, from, currency);
          }}
          onPriceDraft={setPriceDraft}
          onPriceSave={() => {
            if (priceDraft !== null) savePrice.mutate(priceDraft);
          }}
          onPriceCancel={() => {
            setPriceDraft(null);
          }}
          createDraft={createDraft}
          onCreateDraft={setCreateDraft}
          onCreateSubmit={() => {
            create.mutate();
          }}
          onClose={closeDrawer}
        />
      )}

      {/*
       * THE RENAME CONFIRM — §2.4's three honest edges, stated where the act happens
       * rather than hidden. Edge (c) is the refutation's T-P4 resolution: the renewal
       * reminder queue and send path JOIN `products` live, so a rename changes what
       * FUTURE reminder letters say — measured, then asserted in the e2e, not assumed.
       */}
      <ConfirmDialog
        open={pendingRename !== null}
        title={`Rename ${pendingRename?.code ?? ''}?`}
        body={
          `“${pendingRename?.from ?? ''}” becomes “${pendingRename?.to ?? ''}”. Documents ` +
          'already sent or confirmed keep the old name — their lines are snapshots and ' +
          'never rewrite. Two things do change: a draft line without its own typed ' +
          'description picks up the new name if that draft is re-priced, and future ' +
          'renewal reminder letters will name the product as it reads then.'
        }
        confirmLabel="Rename product"
        busy={patch.isPending}
        onConfirm={commitRename}
        onCancel={() => {
          setPendingRename(null);
        }}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────── table furniture ── */

/** One header cell, in the treatment the repo already ruled: `text-th` on `bg-subtle`
 * in `text-fg` (16.14 light / 15.11 dark — `RenewalsPage` derived it in-source). */
function Th({ children, className }: { children: React.ReactNode; className?: string }): React.JSX.Element {
  return (
    <th scope="col" className={cn('px-3 text-th uppercase text-fg', className)}>
      {children}
    </th>
  );
}

/**
 * The price / from cell — the three states, in the register's money slot. Right-aligned,
 * tabular, currency in the string the server formats; a masked seat sees the marker
 * `•••` in `text-fg-secondary` in the same slot at the same size (hidden, not blanked).
 */
function PriceCell({ state }: { state: PriceState }): React.JSX.Element {
  // Unknown is not "not priced here": until the history answers, the cell says so.
  if (state.kind === 'pending') return <span className="text-label text-fg-secondary">Loading price…</span>;
  if (state.kind === 'error') return <span className="text-label text-fg-secondary">Price could not be read.</span>;
  const { lists } = state;
  if (lists.length === 0) {
    return <span className="text-label text-fg-secondary">No price row — this country cannot quote it.</span>;
  }
  // ONE LINE PER LIST (migration 66) — a Congo product priced in CDF and in USD shows both,
  // each with its own flags; a one-list product renders exactly as it always did.
  if (lists.length > 1) {
    return (
      <span className="flex flex-col items-end gap-1.5">
        {lists.map((list) => (
          <ListPrice key={list.currency} history={list} labelled />
        ))}
      </span>
    );
  }
  return <ListPrice history={lists[0] as ProductHistory} labelled={false} />;
}

/** One list's price, its window and its flags — the cell's content for one currency. */
function ListPrice({
  history,
  labelled,
}: {
  history: ProductHistory;
  /** Prefix the currency — only when the cell shows more than one list. */
  labelled: boolean;
}): React.JSX.Element {
  const current = history.current;
  const flags = (
    <span className="flex flex-wrap justify-end gap-1">
      {current !== null && !current.active ? <Chip>Withdrawn in this country</Chip> : null}
      {current?.verify_before_prod === true ? <Chip>Price not confirmed</Chip> : null}
      {history.future.length > 0 ? (
        <Chip>
          {history.future.length === 1
            ? `A change is queued from ${history.future[0]?.effective_from ?? ''}`
            : `${String(history.future.length)} changes queued`}
        </Chip>
      ) : null}
    </span>
  );
  if (current === null) {
    return (
      <span className="flex flex-col items-end gap-1">
        <span className={cn('inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-label font-medium', docToneClasses('warning'))}>
          <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
          {labelled ? `${history.currency}: ` : ''}No price applies today
        </span>
        {flags}
      </span>
    );
  }
  const masked = current.price !== null && isMaskedAmount(current.price);
  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className={cn('tnum text-body', masked || current.priced_per_customer ? 'text-fg-secondary' : 'text-fg')}>
        {current.priced_per_customer && labelled ? `${current.currency}: ` : ''}
        {priceText(current)}
        <span className="text-label text-fg-secondary">
          {' '}
          from {current.effective_from}
          {current.effective_to === null ? '' : ` to ${current.effective_to}`}
        </span>
      </span>
      {flags}
    </span>
  );
}
